import React, {  useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import useTheme from '../../hooks/useTheme';
import Tooltip from '../tooltip/Tooltip';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/*
 * Custom Input Field
 */
const CustomTextField = ({
	id,
	value,
	error,
	label,
	width,
	onChangeText,
	dataTestId = 'custom-textfield',
	required = false,
	onClick,
	onInput,
	autoFocus = false,
	disabled = false,
	pattern,
	maxLength = 32,
	type = 'text', // default type is 'text'
	tooltipText,
	tooltipWidth,
	tooltipPosition,
	onKeyDown,
	multiline = false,
	minRows,
	maxRows,
	endAdornment,
	form,
	helperTextWidth = '100%',
	disabledColor,
	readOnly = false,
	sx,
	placeholder = '',
	InputLabelProps={ },
	autoComplete,
	allowChar,
	warningTextShow = true,
	showPlaceholder = false,
}) => {
	const { FontFace, color } = useTheme('LIGHT');
	const inputRef = useRef(null);
	const [hideTooltip, setHideTooltip] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [maxLengthError, setMaxLengthError] = useState('');
	// Only external validation errors use error styling; max-length helper is a warning.
	const hasValidationError = !disabled && !!error;

const handleChange = (event) => {
		const newValue = event.target.value;
		const DISALLOWED_CHARS = ['<', '>', '/', '\\'];
		let disallowedRegex;
		if (allowChar) {
			const allowCharSet = new Set(
				typeof allowChar === 'string' ? allowChar.split('') : allowChar
			);
			const escapeForCharClass = (char) =>
				/[-\\^$*+?.()|[\]{}]/.test(char) ? '\\' + char : char;
			const filteredChars = DISALLOWED_CHARS.filter((char) => !allowCharSet.has(char));
			if (filteredChars.length > 0) {
				disallowedRegex = new RegExp(`[${filteredChars.map(escapeForCharClass).join('')}]`, 'g');
			}
		} else {
			disallowedRegex = /[<>\/\\]/g;
		}
		const sanitizedValue = disallowedRegex ? newValue.replace(disallowedRegex, '') : newValue;
		if (sanitizedValue !== newValue) {
			event.target.value = sanitizedValue; // override the value before calling
		}
	
		if (pattern) {
			if (pattern.test(sanitizedValue)) {
				onChangeText(event);
			}
		} else {
			onChangeText(event);
		}

		if (newValue?.length >= maxLength && warningTextShow) {
			setMaxLengthError(`Up to ${maxLength} characters only.`);
		} else {
			setMaxLengthError('');
		}
	};

	const handleTextFiledClick = () => {
		setHideTooltip(true);
	};
	const handleMouseEnter = () => {
		setHideTooltip(false); // Show tooltip when mouse enters
	};

	// Add the onKeyDown handler
	const handleKeyDown = (event) => {
		const RESTRICTED_KEYS = ['<', '>', '/', '\\'];
		let restrictedKeys;
		if (allowChar) {
			const allowCharSet = new Set(
				typeof allowChar === 'string' ? allowChar.split('') : allowChar
			);
			restrictedKeys = RESTRICTED_KEYS.filter((char) => !allowCharSet.has(char));
		} else {
			restrictedKeys = RESTRICTED_KEYS;
		}
		if (restrictedKeys.includes(event.key)) {
			event.preventDefault();
			return;
		}
		if (event.key === 'Enter' && onKeyDown) {
			onKeyDown(event);
		}
	};

	const textFieldProps = {
		value: value,
		onChange: handleChange,
		required: required,
		type: type, // Now type can be passed as a prop
		disabled: disabled,
		id: id,
		error: hasValidationError,
		helperText: disabled ? '' : (error || maxLengthError),		
		label: label,
		variant: 'standard',
		'data-testid': dataTestId,
		inputRef: inputRef,
		autoComplete: autoComplete || 'off',
		onClick: (e) => {
			handleTextFiledClick();
			if (onClick) onClick(e);
		},
		// placeholder: placeholder,
		// placeholder: InputLabelProps?.shrink === false ? ' ' : placeholder,
		placeholder: showPlaceholder ? placeholder : InputLabelProps?.shrink === false ? ' ' : placeholder,
		onMouseEnter: handleMouseEnter,
		onInput: onInput,
		autoFocus: autoFocus,
		onKeyDown: handleKeyDown,
		inputProps: {
			maxLength: maxLength,
		},
		FormHelperTextProps: {
			style: {
				fontFamily: 'Inter-Regular',
				color: error ? color.text.error : color.text.warning,
				fontSize: FontFace.font_size.xx_small,
				lineHeight: '1.2',
				whiteSpace: 'normal', // Allow text to wrap
				wordWrap: 'break-word', // Break words properly
				width: helperTextWidth, // Make helper text same width as TextField
				display: 'block',
				},
		},
		InputLabelProps: {
			shrink: InputLabelProps?.shrink ?? Boolean(value),
			// shrink: InputLabelProps?.shrink ?? Boolean(value) ?? true,
			style: {
				fontFamily: 'Inter-Regular',
				fontSize: FontFace.font_size.input_text,
				...InputLabelProps.style,
				...(hasValidationError ? { color: color.text.error } : { color: color.text.secondary }),
			},
		},
		sx: {
			width: width,
			height: '48px',
			'& .MuiInputBase-input': {
				// color: color.text.secondary,
				fontSize: form === 'dynamicForm' ? FontFace.font_size.x_small : FontFace.font_size.input_text,
				fontFamily: 'Inter-Regular',
				color: '#121212',
				cursor: disabled ? 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\'><text x=\'0\' y=\'20\' font-size=\'20\' fill=\'red\'>🚫</text></svg>"), not-allowed' : '',
			},
			'& .MuiInputLabel-root': {
				color: color.text.secondary,
				fontFamily: 'Inter-Regular',
				fontSize: FontFace.font_size.small_medium,
			},
			'& .MuiInputLabel-root.Mui-error': {
				color: color.text.error,
			},
			'& label': {
				fontFamily: 'Inter-Regular',
				fontSize: FontFace.font_size.xx_inputlabel,
			},
			'& input': {
				fontFamily: 'Inter-Regular',
				fontSize: FontFace.font_size.input_text,
			},
			'& .MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
				color: '#121212',
				fontSize: FontFace.font_size.large,
			},
			'& .MuiInputLabel-root.Mui-error.Mui-focused': {
				color: color.text.error,
			},
			'& .MuiInput-underline:before': {
				borderBottomColor: color.input.line,
			},
			'& .MuiInput-underline:after': {
				borderBottomColor: color.input.line_active,
				backgroundColor: color.input.background_active,
				borderWidth: '2px',
			},
			'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
				borderBottomColor: color.input.line_active,
				borderBottomWidth: '2px',
			},
			'&.Mui-error .MuiInput-underline:before': {
				borderBottomColor: color.text.error,
			},
			'&.Mui-error .MuiInput-underline:after': {
				borderBottomColor: color.text.error,
				backgroundColor: 'transparent',
			},
			'&.Mui-error .MuiInput-underline:hover:not(.Mui-disabled):before': {
				borderBottomColor: color.text.error,
				borderBottomWidth: '2px',
			},
			'& input:-webkit-autofill': {
				WebkitBoxShadow: '0 0 0px 1000px white inset',
			},
			'& input::placeholder': {
				opacity: 1,
				color: '#b2b2b2 !important',
			},
			// Ensure disabled field still shows placeholder
			'& .MuiInputBase-input.Mui-disabled::placeholder': {
				color: '#b2b2b2 !important',
				opacity: 1,
			},
			 "& .MuiInputBase-input.Mui-disabled": disabledColor && {
				fontFamily: 'Inter-Regular',
      			color: disabledColor, // Keep text black
      			WebkitTextFillColor: disabledColor, // Ensures text color stays black in WebKit browsers
    		},
			...(readOnly && {
				pointerEvents: "none",
				"& .MuiInput-underline:after": {
					borderBottom: "none", // Prevents the focus effect (purple underline)
				},
			}),
			...sx,
		},
		size: 'small',
		margin: 'none',
		multiline,
		minRows,
		maxRows,
		InputProps: {
			endAdornment: endAdornment,
		},
	};

	return (
		<>
			{tooltipText ? (
				<Tooltip
					content={tooltipText}
					type="basic"
					direction={tooltipPosition ?? 'top'}
					delay={300}
					style={{ width: tooltipWidth ?? 'auto' }}
					hideTooltip={hideTooltip}
					
				>
					<TextField
						{...textFieldProps}
						type={
							type === 'password'
							? (showPassword ? 'text' : 'password')
							: 'text'
						}
						InputProps={
							type === 'password'
							? {
								endAdornment: (
									<InputAdornment position="end">
									<IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
									</InputAdornment>
								),
								}
							: type === 'upload'
							? {
								endAdornment: (
									<InputAdornment position="end">
									<IconButton onClick={() => console.log("Upload clicked")} edge="end">
										{/* Any icon or even empty button */}
									</IconButton>
									</InputAdornment>
								),
								}
							: textFieldProps.InputProps
						}
						/>

				</Tooltip>
			) : (
				<TextField
					{...textFieldProps}
					type={
						type === 'password'
						? (showPassword ? 'text' : 'password')
						: 'text'
					}
					InputProps={
						type === 'password'
						? {
							endAdornment: (
								<InputAdornment position="end">
								<IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
								</InputAdornment>
							),
							}
						: type === 'upload'
						? {
							endAdornment: (
								<InputAdornment position="end">
								<IconButton onClick={() => console.log("Upload clicked")} edge="end">
									{/* Any icon or even empty button */}
								</IconButton>
								</InputAdornment>
							),
							}
						: textFieldProps.InputProps
					}
					/>

			)}
		</>
	);
};

CustomTextField.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	width: PropTypes.number.isRequired,
	onChangeText: PropTypes.func.isRequired,
	dataTestId: PropTypes.string.isRequired,
	required: PropTypes.bool,
	multiline: PropTypes.bool,
	error: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	minRows: PropTypes.number,
	maxRows: PropTypes.number,
	onClick: PropTypes.func,
	onInput: PropTypes.func,
	autoFocus: PropTypes.bool,
	disabled: PropTypes.bool,
	pattern: PropTypes.string,
	maxLength: PropTypes.number,
	type: PropTypes.string, // Add type as a prop
	tooltipText: PropTypes.string,
	tooltipWidth: PropTypes.string,
	tooltipPosition: PropTypes.string,
	onKeyDown: PropTypes.func,
	endAdornment: PropTypes.object,
	form: PropTypes.string,
	helperTextWidth: PropTypes.string,
	disabledColor: PropTypes.string,
	sx: PropTypes.object,
	placeholder: PropTypes.string,
	InputLabelProps: PropTypes.object,
	readOnly: PropTypes.bool,
	autoComplete: PropTypes.string,
	allowChar: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
	  ]),
	warningTextShow: PropTypes.bool,
	showPlaceholder: PropTypes.bool,
};

export default CustomTextField;
